import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Heart,
  Users,
  User,
  Lock,
  Globe,
  Calendar,
  Search,
  X,
  Award
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  recipient_ids: [],
  recipient_names: [],
  is_team: false,
  team_name: "",
  what_they_did: "",
  impact: "",
  is_private: false,
  date: new Date().toISOString().split("T")[0]
};

export const ShoutOuts = ({ user }) => {
  const [shoutOuts, setShoutOuts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShoutOut, setSelectedShoutOut] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    fetchShoutOuts();
    fetchUsers();
  }, []);

  const fetchShoutOuts = async () => {
    try {
      const response = await axios.get(`${API}/shout-outs`);
      setShoutOuts(response.data);
    } catch (error) {
      toast.error("Failed to fetch shout outs");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data.filter(u => u.status === "approved"));
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.is_team && formData.recipient_ids.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    if (formData.is_team && !formData.team_name.trim()) {
      toast.error("Please enter a team name");
      return;
    }
    
    if (!formData.what_they_did.trim()) {
      toast.error("Please describe what they did");
      return;
    }
    
    try {
      if (isEditing && selectedShoutOut) {
        await axios.put(`${API}/shout-outs/${selectedShoutOut.id}`, formData);
        toast.success("Shout out updated!");
      } else {
        await axios.post(`${API}/shout-outs`, formData);
        toast.success("Shout out sent!");
      }
      setDialogOpen(false);
      resetForm();
      fetchShoutOuts();
    } catch (error) {
      toast.error(isEditing ? "Failed to update shout out" : "Failed to send shout out");
    }
  };

  const handleDelete = async () => {
    if (!selectedShoutOut) return;
    try {
      await axios.delete(`${API}/shout-outs/${selectedShoutOut.id}`);
      toast.success("Shout out deleted");
      setDeleteDialogOpen(false);
      setSelectedShoutOut(null);
      fetchShoutOuts();
    } catch (error) {
      toast.error("Failed to delete shout out");
    }
  };

  const handleEdit = (shoutOut) => {
    setSelectedShoutOut(shoutOut);
    setFormData({
      recipient_ids: shoutOut.recipient_ids || [],
      recipient_names: shoutOut.recipient_names || [],
      is_team: shoutOut.is_team || false,
      team_name: shoutOut.team_name || "",
      what_they_did: shoutOut.what_they_did || "",
      impact: shoutOut.impact || "",
      is_private: shoutOut.is_private || false,
      date: shoutOut.date || new Date().toISOString().split("T")[0]
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedShoutOut(null);
    setIsEditing(false);
    setUserSearch("");
  };

  const toggleRecipient = (selectedUser) => {
    const isSelected = formData.recipient_ids.includes(selectedUser.id);
    if (isSelected) {
      setFormData({
        ...formData,
        recipient_ids: formData.recipient_ids.filter(id => id !== selectedUser.id),
        recipient_names: formData.recipient_names.filter(name => name !== selectedUser.name)
      });
    } else {
      setFormData({
        ...formData,
        recipient_ids: [...formData.recipient_ids, selectedUser.id],
        recipient_names: [...formData.recipient_names, selectedUser.name]
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // Filter shout outs
  const filteredShoutOuts = shoutOuts.filter((so) => {
    const matchesSearch = 
      (so.author_name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (so.recipient_names?.join(", ").toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (so.what_they_did?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (so.team_name?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "given") return matchesSearch && so.author_id === user?.id;
    if (activeTab === "received") return matchesSearch && so.recipient_ids?.includes(user?.id);
    return matchesSearch;
  });

  // Filter users for selection (exclude current user)
  const filteredUsers = allUsers.filter(u => 
    u.id !== user?.id &&
    (u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
     u.email?.toLowerCase().includes(userSearch.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="shoutouts-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shoutouts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Heart className="h-8 w-8 text-pink-500 fill-pink-500" />
            Shout Outs
          </h1>
          <p className="text-muted-foreground mt-1">
            Recognize and thank your colleagues
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="create-shoutout-btn"
          className="btn-animate bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
        >
          <Plus className="mr-2 h-4 w-4" /> Give a Shout Out
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search shout outs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-shoutouts"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="shoutout-tabs">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({shoutOuts.length})
          </TabsTrigger>
          <TabsTrigger value="given" data-testid="tab-given">
            Given ({shoutOuts.filter(so => so.author_id === user?.id).length})
          </TabsTrigger>
          <TabsTrigger value="received" data-testid="tab-received">
            Received ({shoutOuts.filter(so => so.recipient_ids?.includes(user?.id)).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredShoutOuts.length === 0 ? (
            <Card className="border-stone-200" data-testid="no-shoutouts">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Heart className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
                <h3 className="font-medium text-lg">No shout outs found</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  {activeTab === "given" 
                    ? "You haven't given any shout outs yet"
                    : activeTab === "received"
                    ? "You haven't received any shout outs yet"
                    : "Be the first to thank a colleague!"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredShoutOuts.map((shoutOut, index) => (
                <Card
                  key={shoutOut.id}
                  className="card-hover border-stone-200 animate-slide-up overflow-hidden"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`shoutout-card-${shoutOut.id}`}
                >
                  {/* Gradient Header */}
                  <div className="h-2 bg-gradient-to-r from-pink-400 via-rose-400 to-red-400" />
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        {/* Author */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span>From: <span className="font-medium text-foreground">{shoutOut.author_name}</span></span>
                        </div>
                        
                        {/* Recipients */}
                        <div className="flex items-center gap-2">
                          {shoutOut.is_team ? (
                            <Users className="h-5 w-5 text-pink-500" />
                          ) : (
                            <Award className="h-5 w-5 text-pink-500" />
                          )}
                          <CardTitle className="font-heading text-lg">
                            {shoutOut.is_team 
                              ? shoutOut.team_name 
                              : shoutOut.recipient_names?.join(", ") || "Unknown"}
                          </CardTitle>
                        </div>
                      </div>
                      
                      {/* Actions (only for author) */}
                      {shoutOut.author_id === user?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`shoutout-menu-${shoutOut.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(shoutOut)}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedShoutOut(shoutOut);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {/* What they did */}
                    <div className="mb-3">
                      <p className="text-sm font-medium text-muted-foreground mb-1">What they did:</p>
                      <p className="text-sm">
                        {shoutOut.what_they_did === "[Private]" 
                          ? <span className="italic text-muted-foreground">(Details kept private)</span>
                          : shoutOut.what_they_did}
                      </p>
                    </div>
                    
                    {/* Impact */}
                    {shoutOut.impact && shoutOut.impact !== "[Private]" && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-muted-foreground mb-1">Impact:</p>
                        <p className="text-sm text-pink-700">{shoutOut.impact}</p>
                      </div>
                    )}
                    
                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-stone-100 mt-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(shoutOut.date)}
                      </div>
                      <div className="flex items-center gap-2">
                        {shoutOut.is_team && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            <Users className="h-3 w-3 mr-1" /> Team
                          </Badge>
                        )}
                        {shoutOut.is_private && (
                          <Badge variant="outline" className="text-xs bg-stone-50 text-stone-600 border-stone-200">
                            <Lock className="h-3 w-3 mr-1" /> Private
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Heart className="h-6 w-6 text-pink-500 fill-pink-500" />
              {isEditing ? "Edit Shout Out" : "Give a Shout Out"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update your recognition" : "Recognize a colleague for their great work"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* Team vs Individual Toggle */}
            <div className="form-section">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is_team">Recognize a Team</Label>
                  <p className="text-sm text-muted-foreground">
                    Toggle on to thank a group/team instead of individuals
                  </p>
                </div>
                <Switch
                  id="is_team"
                  checked={formData.is_team}
                  onCheckedChange={(checked) => setFormData({ 
                    ...formData, 
                    is_team: checked,
                    recipient_ids: checked ? [] : formData.recipient_ids,
                    recipient_names: checked ? [] : formData.recipient_names
                  })}
                  data-testid="is-team-toggle"
                />
              </div>
            </div>
            
            {/* Team Name (if team) */}
            {formData.is_team ? (
              <div className="form-section">
                <Label htmlFor="team_name">Team Name *</Label>
                <Input
                  id="team_name"
                  value={formData.team_name}
                  onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                  placeholder="e.g., Children's Services Team, Summer Reading Volunteers"
                  data-testid="team-name-input"
                />
              </div>
            ) : (
              /* Individual Recipients */
              <div className="form-section">
                <Label>Select Recipients *</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Choose one or more colleagues to thank
                </p>
                
                {/* Selected Recipients */}
                {formData.recipient_names.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.recipient_names.map((name, idx) => (
                      <Badge key={idx} className="px-3 py-1 bg-pink-100 text-pink-700">
                        {name}
                        <button
                          type="button"
                          onClick={() => {
                            const id = formData.recipient_ids[idx];
                            setFormData({
                              ...formData,
                              recipient_ids: formData.recipient_ids.filter(i => i !== id),
                              recipient_names: formData.recipient_names.filter((_, i) => i !== idx)
                            });
                          }}
                          className="ml-2 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* User Search */}
                <Input
                  placeholder="Search for colleagues..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="mb-2"
                  data-testid="user-search-input"
                />
                
                {/* User List */}
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {filteredUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No users found
                    </p>
                  ) : (
                    filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                          formData.recipient_ids.includes(u.id)
                            ? "bg-pink-50 border border-pink-200"
                            : "hover:bg-muted"
                        }`}
                        onClick={() => toggleRecipient(u)}
                        data-testid={`user-option-${u.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{u.name}</span>
                        </div>
                        {formData.recipient_ids.includes(u.id) && (
                          <Badge className="bg-pink-500 text-white">Selected</Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            
            {/* What they did */}
            <div className="form-section">
              <Label htmlFor="what_they_did">What did they do? *</Label>
              <Textarea
                id="what_they_did"
                value={formData.what_they_did}
                onChange={(e) => setFormData({ ...formData, what_they_did: e.target.value })}
                placeholder="Describe the action or achievement..."
                rows={3}
                required
                data-testid="what-they-did-input"
              />
            </div>
            
            {/* Impact */}
            <div className="form-section">
              <Label htmlFor="impact">Impact / Why it matters</Label>
              <Textarea
                id="impact"
                value={formData.impact}
                onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                placeholder="How did this help? What difference did it make?"
                rows={2}
                data-testid="impact-input"
              />
            </div>
            
            {/* Date */}
            <div className="form-section">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                data-testid="shoutout-date-input"
              />
            </div>
            
            {/* Privacy Toggle */}
            <div className="form-section p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {formData.is_private ? (
                    <Lock className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Globe className="h-5 w-5 text-green-600" />
                  )}
                  <div>
                    <Label htmlFor="is_private" className="cursor-pointer">
                      {formData.is_private ? "Private Shout Out" : "Public Shout Out"}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {formData.is_private 
                        ? "Only you and the recipients can see the details"
                        : "All staff can see the full recognition"}
                    </p>
                  </div>
                </div>
                <Switch
                  id="is_private"
                  checked={formData.is_private}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
                  data-testid="is-private-toggle"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                data-testid="submit-shoutout-btn"
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
              >
                <Heart className="mr-2 h-4 w-4" />
                {isEditing ? "Update Shout Out" : "Send Shout Out"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shout Out</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this shout out? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-shoutout"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
